import {
  CalibrationAnswers,
  DerivedAthleteParams,
} from '../types/athleteProfile'

export const DEFAULT_CALIBRATION: CalibrationAnswers = {
  flatEffort: 'steady', // 6:00-6:30
  uphillStrategy: 'mixed',
  downhillHandling: 'controlled',
  fatigue: 'mid', // 3-5h
  riskTolerance: 'balanced',
  heatTolerance: 'moderate',
  sweatRate: 'normal',
  coldStart: 'no',
}

export function getDefaultCalibrationAnswers(): CalibrationAnswers {
  return { ...DEFAULT_CALIBRATION }
}

export function deriveAthleteParams(
  answers: CalibrationAnswers,
): DerivedAthleteParams {
  const params: DerivedAthleteParams = {
    // Defaults
    flatPaceMinPerKm: 6.25,
    uphillCostMultiplier: 1.0,
    hikeGradeThreshold: 10,
    downhillBonusMultiplier: 1.0,
    technicalPenaltyMultiplier: 1.0,
    fatigueOnsetMinutes: 300,
    fatigueCurveExponent: 2.0,
    maxFatigueMultiplier: 0.25,
    lateRacePacingPenalty: 0.08,
    absorptionDecay: 0.1,
    scenarioBias: 0,
    heatPacingPenalty: 1.0,
    hydrationMultiplier: 1.0,
    sodiumMultiplier: 1.0,
    fluidMultiplier: 1.0,
    carbAbsorptionModifier: 1.0,
    coldStartPenalty: 0,
    baselineChoPerHourG: 70,
    baselineCalPerHourKcal: 280,
    baselineFluidMlPerHour: 550,
  }

  // 1. Sustainable Flat Effort
  // >6:30 → 6:45 min/km, 60 g/h, 500 ml/h
  // 6:00–6:30 → 6:15 min/km, 70 g/h, 550 ml/h
  // 5:30–6:00 → 5:45 min/km, 80 g/h, 600 ml/h
  // <5:30 → 5:15 min/km, 90 g/h, 650 ml/h
  switch (answers.flatEffort) {
    case 'easy': // >6:30
      params.flatPaceMinPerKm = 6.75 // 6:45
      params.baselineChoPerHourG = 60
      params.baselineFluidMlPerHour = 500
      break
    case 'comfortable': // 6:00-6:30
      params.flatPaceMinPerKm = 6.25 // 6:15
      params.baselineChoPerHourG = 70
      params.baselineFluidMlPerHour = 550
      break
    case 'steady': // 5:30-6:00
      params.flatPaceMinPerKm = 5.75 // 5:45
      params.baselineChoPerHourG = 80
      params.baselineFluidMlPerHour = 600
      break
    case 'sub530': // <5:30
      params.flatPaceMinPerKm = 5.25 // 5:15
      params.baselineChoPerHourG = 90
      params.baselineFluidMlPerHour = 650
      break
  }
  params.baselineCalPerHourKcal = params.baselineChoPerHourG * 4 // Approx 4kcal/g

  // 2. Uphill Strategy
  switch (answers.uphillStrategy) {
    case 'hikeEarly':
      params.uphillCostMultiplier = 0.85 // Conservative climb curve (slower run, earlier hike)
      params.hikeGradeThreshold = 8
      break
    case 'mixed':
      params.uphillCostMultiplier = 1.0
      params.hikeGradeThreshold = 12
      break
    case 'runMost':
      params.uphillCostMultiplier = 1.15 // Aggressive
      params.hikeGradeThreshold = 15
      break
  }

  // 3. Downhill Handling
  switch (answers.downhillHandling) {
    case 'cautious':
      params.downhillBonusMultiplier = 0.5
      params.technicalPenaltyMultiplier = 1.3
      break
    case 'controlled':
      params.downhillBonusMultiplier = 0.8
      params.technicalPenaltyMultiplier = 1.1
      break
    case 'aggressive':
      params.downhillBonusMultiplier = 1.2
      params.technicalPenaltyMultiplier = 0.9
      break
  }

  // 4. Fatigue Onset
  // <3h: 12% late-race penalty, -15% absorption
  // 3-5h: 10%, -12%
  // 5-7h: 8%, -8%
  // 8h+: 6%, -5%
  switch (answers.fatigue) {
    case 'early': // <3h
      params.fatigueOnsetMinutes = 180
      params.lateRacePacingPenalty = 0.12
      params.absorptionDecay = 0.15
      params.fatigueCurveExponent = 2.5
      params.maxFatigueMultiplier = 0.3
      break
    case 'mid': // 3-5h
      params.fatigueOnsetMinutes = 240
      params.lateRacePacingPenalty = 0.1
      params.absorptionDecay = 0.12
      params.fatigueCurveExponent = 2.0
      params.maxFatigueMultiplier = 0.25
      break
    case 'late': // 5-7h
      params.fatigueOnsetMinutes = 360
      params.lateRacePacingPenalty = 0.08
      params.absorptionDecay = 0.08
      params.fatigueCurveExponent = 1.5
      params.maxFatigueMultiplier = 0.2
      break
    case 'ultra': // 8h+
      params.fatigueOnsetMinutes = 480
      params.lateRacePacingPenalty = 0.06
      params.absorptionDecay = 0.05
      params.fatigueCurveExponent = 1.2
      params.maxFatigueMultiplier = 0.15
      break
  }

  // 5. Risk Tolerance
  switch (answers.riskTolerance) {
    case 'finish':
      params.scenarioBias = -0.05 // Biases towards Safe
      break
    case 'balanced':
      params.scenarioBias = 0
      break
    case 'performance':
      params.scenarioBias = 0.05 // Biases towards Optimal
      break
  }

  // 6. Heat Tolerance
  switch (answers.heatTolerance) {
    case 'sensitive':
      params.heatPacingPenalty = 1.5
      params.hydrationMultiplier = 1.3
      params.carbAbsorptionModifier = 0.9
      break
    case 'moderate':
      params.heatPacingPenalty = 1.0
      params.hydrationMultiplier = 1.0
      params.carbAbsorptionModifier = 1.0
      break
    case 'tolerant':
      params.heatPacingPenalty = 0.7
      params.hydrationMultiplier = 0.9
      params.carbAbsorptionModifier = 1.05
      break
  }

  // 7. Sweat Rate
  switch (answers.sweatRate) {
    case 'heavy':
      params.sodiumMultiplier = 1.4
      params.fluidMultiplier = 1.3
      break
    case 'normal':
      params.sodiumMultiplier = 1.0
      params.fluidMultiplier = 1.0
      break
    case 'low':
      params.sodiumMultiplier = 0.7
      params.fluidMultiplier = 0.8
      break
  }

  // 8. Cold Start
  switch (answers.coldStart) {
    case 'yes':
      params.coldStartPenalty = 0.05
      break
    case 'some':
      params.coldStartPenalty = 0.02
      break
    case 'no':
      params.coldStartPenalty = 0
      break
  }

  return params
}
